import React, { useState, useEffect } from 'react';
import './MarketResearchPage.css';

const MarketResearchPage = () => {
  const [activeTab, setActiveTab] = useState('analyze');
  const [analysisUrl, setAnalysisUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [recentAnalyses, setRecentAnalyses] = useState([]);
  const [aiInsights, setAiInsights] = useState(null);
  const [isLoadingInsights, setIsLoadingInsights] = useState(false);
  const [analysisError, setAnalysisError] = useState(null);
  const [insightsError, setInsightsError] = useState(null);
  
  // Enhanced data extraction states
  const [rawHtmlContent, setRawHtmlContent] = useState(null);
  const [enhancedData, setEnhancedData] = useState(null);
  const [showPageModal, setShowPageModal] = useState(false);
  const [currentAnalyzedUrl, setCurrentAnalyzedUrl] = useState('');

  // Direct SEO API endpoint - using production API
  const SEO_API_BASE = 'https://api.seoblogger.io';

  // Enhanced data extraction function
  const extractEnhancedData = (data) => {
    if (!data) return null;

    const enhanced = {
      pageContent: {
        title: data.seoIntelligence?.titleStrategy?.title || '',
        metaDescription: data.seoIntelligence?.metaStrategy?.description || '',
        htmlContent: data.htmlContent || data.rawHtml || '',
        allMetaTags: data.metaTags || [],
        allImages: data.images || [],
        allLinks: data.links || [],
        headings: data.headings || [],
        socialMetaTags: data.socialMeta || {},
        structuredData: data.structuredData || [],
        pageSize: data.pageStats?.size || 0,
        loadTime: data.pageStats?.loadTime || 0
      },
      technicalDetails: {
        charset: data.technicalInfo?.charset || '',
        language: data.technicalInfo?.language || '',
        viewport: data.technicalInfo?.viewport || '',
        robots: data.technicalInfo?.robots || '',
        canonical: data.technicalInfo?.canonical || '',
        hreflang: data.technicalInfo?.hreflang || [],
        scripts: data.technicalInfo?.scripts || [],
        stylesheets: data.technicalInfo?.stylesheets || []
      }
    };

    return enhanced;
  };

  // Format SEO data into tabular format
  const formatSEODataForTables = (data) => {
    if (!data) return null;

    // SEO Content Analysis Table
    const seoTable = {
      headers: ["Element", "Content", "Length", "Status"],
      rows: []
    };

    // Title Analysis
    const titleData = data.seoIntelligence?.titleStrategy || {};
    const titleLength = titleData.length || 0;
    const titleStatus = titleLength >= 30 && titleLength <= 60 ? "✅ Optimal" : "⚠️ Needs Work";
    seoTable.rows.push([
      "Page Title", 
      titleData.title || "Not found",
      `${titleLength} chars`, 
      titleStatus
    ]);

    // Meta Description
    const metaData = data.seoIntelligence?.metaStrategy || {};
    const metaLength = metaData.length || 0;
    const metaOptimized = metaData.isOptimized || false;
    const metaStatus = metaOptimized ? "✅ Optimized" : "⚠️ Needs Work";
    seoTable.rows.push([
      "Meta Description", 
      metaData.description || "Not found",
      `${metaLength} chars`, 
      metaStatus
    ]);

    // Heading Structure
    const contentArch = data.seoIntelligence?.contentArchitecture || {};
    const headingStructure = contentArch.headingStructure || {};
    const h1Count = headingStructure.h1Tags || 0;
    const h1Status = h1Count === 1 ? "✅ Perfect" : h1Count === 0 ? "❌ Missing" : "⚠️ Too Many";
    seoTable.rows.push([
      "H1 Tags", 
      `${h1Count} H1 tags found`,
      `Total: ${headingStructure.totalHeadings || 0} headings`, 
      h1Status
    ]);

    // Heading breakdown
    if (headingStructure.h2Tags || headingStructure.h3Tags) {
      seoTable.rows.push([
        "Heading Structure", 
        `H2: ${headingStructure.h2Tags || 0}, H3: ${headingStructure.h3Tags || 0}`,
        `Density: ${headingStructure.headingDensity || 0}%`, 
        "📊 Structure"
      ]);
    }

    // Market Intelligence Table
    const marketTable = {
      headers: ["Category", "Analysis", "Confidence", "Details"],
      rows: []
    };

    const primaryIndustry = data.marketIntelligence?.primaryIndustry || "Unknown";
    const industryClassification = data.marketIntelligence?.industryClassification || {};
    
    // Primary Industry
    let primaryConfidence = "0%";
    if (primaryIndustry in industryClassification) {
      primaryConfidence = industryClassification[primaryIndustry]?.confidence + "%" || "0%";
    }
    marketTable.rows.push([
      "Primary Industry",
      primaryIndustry.charAt(0).toUpperCase() + primaryIndustry.slice(1),
      primaryConfidence,
      "🏢 Main business sector"
    ]);

    // Business Model
    const businessModel = data.businessIntelligence?.businessModel || [];
    if (businessModel.length > 0) {
      marketTable.rows.push([
        "Business Model",
        businessModel.join(", "),
        "High",
        "💼 Revenue strategy"
      ]);
    }

    // Target Audience
    const targetAudience = data.businessIntelligence?.targetAudience || [];
    if (targetAudience.length > 0) {
      marketTable.rows.push([
        "Target Audience",
        targetAudience.slice(0, 3).join(", "),
        "Medium",
        "👥 Primary users"
      ]);
    }

    // Market Opportunity
    const opportunityScore = data.opportunityAnalysis?.marketOpportunityScore || 0;
    const opportunityStatus = opportunityScore >= 70 ? "🟢 High" : opportunityScore >= 40 ? "🟡 Medium" : "🔴 Low";
    marketTable.rows.push([
      "Market Opportunity",
      `${opportunityScore}/100 points`,
      opportunityStatus,
      "📈 Growth potential"
    ]);

    // Keywords Table
    const keywordsTable = {
      headers: ["Keyword", "Frequency", "Type", "Opportunity"],
      rows: []
    };

    const topKeywords = data.keywordIntelligence?.topKeywords?.slice(0, 8) || [];
    const commercialKeywords = data.keywordIntelligence?.commercialKeywords || [];
    const commercialSet = new Set(commercialKeywords.map(kw => kw.keyword));

    // Add top commercial keywords first
    commercialKeywords.slice(0, 5).forEach(kw => {
      keywordsTable.rows.push([
        kw.keyword,
        kw.frequency?.toString() || "N/A",
        "💰 Commercial",
        "🎯 High Value"
      ]);
    });

    // Add top informational keywords
    topKeywords
      .filter(kw => !commercialSet.has(kw.keyword))
      .slice(0, 5)
      .forEach(kw => {
        keywordsTable.rows.push([
          kw.keyword,
          kw.frequency.toString(),
          "📝 Informational",
          "📊 Content Opportunity"
        ]);
      });

    return {
      seo_metrics: seoTable,
      market_analysis: marketTable,
      keyword_analysis: keywordsTable,
      summary: {
        domain: data.domain || "",
        primary_industry: primaryIndustry,
        opportunity_score: opportunityScore,
        total_keywords: data.keywordIntelligence?.totalKeywords || 0,
        commercial_intent_score: data.keywordIntelligence?.commercialIntentScore || 0
      }
    };
  };

  // Load recent analyses from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('recentAnalyses');
    if (saved) {
      setRecentAnalyses(JSON.parse(saved));
    }
  }, []);

  // Save analysis to recent analyses
  const saveToRecent = (analysis) => {
    const newAnalyses = [analysis, ...recentAnalyses.slice(0, 9)]; // Keep last 10
    setRecentAnalyses(newAnalyses);
    localStorage.setItem('recentAnalyses', JSON.stringify(newAnalyses));
  };

  // Get AI insights for analysis
  const getAIInsights = async (url) => {
    setIsLoadingInsights(true);
    setInsightsError(null);
    try {
      console.log('Fetching AI insights from:', `${SEO_API_BASE}/api/agent/full-analysis`);
      const response = await fetch(`${SEO_API_BASE}/api/agent/full-analysis`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ url }),
      });

      console.log('AI insights response status:', response.status);
      
      if (response.ok) {
        const response_data = await response.json();
        console.log('Full AI response:', response_data);
        // Extract the agent insights from the nested structure
        const insights = response_data.data?.agentInsights || response_data.agentInsights || response_data;
        setAiInsights(insights);
        console.log('AI Insights extracted:', insights);
      } else {
        const errorText = await response.text();
        setInsightsError(`AI insights failed: ${response.status} - ${errorText}`);
        console.error('AI insights error:', response.status, errorText);
      }
    } catch (error) {
      setInsightsError(`AI insights error: ${error.message}`);
      console.error('Error getting AI insights:', error);
    } finally {
      setIsLoadingInsights(false);
    }
  };

  // Analyze website
  const handleAnalyze = async () => {
    if (!analysisUrl.trim()) return;

    setIsLoading(true);
    setAiInsights(null); // Clear previous insights
    setAnalysisError(null);
    setInsightsError(null);
    setRawHtmlContent(null); // Clear previous HTML content
    setEnhancedData(null); // Clear previous enhanced data
    setCurrentAnalyzedUrl(''); // Clear previous URL
    try {
      console.log('Analyzing URL:', analysisUrl);
      console.log('API endpoint:', `${SEO_API_BASE}/api/analyze/competitor`);
      
      const response = await fetch(`${SEO_API_BASE}/api/analyze/competitor`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ url: analysisUrl }),
      });

      console.log('Analysis response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Analysis failed: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      
      // Transform the data to match the expected format
      const transformedData = {
        success: data.success,
        analysis: data.data,
        formatted_data: formatSEODataForTables(data.data)
      };
      
      // Extract enhanced data for new features
      const enhanced = extractEnhancedData(data.data);
      setEnhancedData(enhanced);
      setRawHtmlContent(data.data?.htmlContent || data.data?.rawHtml || '');
      setCurrentAnalyzedUrl(analysisUrl);
      
      setAnalysisResult(transformedData);
      
      // Save to recent analyses
      saveToRecent({
        url: analysisUrl,
        domain: transformedData.analysis?.domain || analysisUrl,
        timestamp: new Date().toISOString(),
        score: transformedData.analysis?.opportunityAnalysis?.marketOpportunityScore || 0,
      });

      // Initialize chat with welcome message
      setChatMessages([{
        id: 1,
        type: 'bot',
        message: `I've analyzed ${transformedData.analysis?.domain || analysisUrl}! I can help you understand the SEO insights, market opportunities, and competitive positioning. What would you like to know?`,
        timestamp: new Date().toISOString(),
      }]);

      setActiveTab('results');

      // Get AI insights after successful analysis
      getAIInsights(analysisUrl);

    } catch (error) {
      console.error('Analysis error:', error);
      setAnalysisError(error.message);
      setAnalysisResult({
        success: false,
        error: error.message,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Simple chat interface (no backend integration for now)
  const handleChatSubmit = async (e) => {
    e.preventDefault();
    if (!chatInput.trim() || !analysisResult?.success) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      message: chatInput,
      timestamp: new Date().toISOString(),
    };

    setChatMessages(prev => [...prev, userMessage]);
    const currentInput = chatInput;
    setChatInput('');

    // Simple response based on analysis data
    setTimeout(() => {
      let response = "I can see the analysis data! ";
      const analysis = analysisResult.analysis;
      
      if (currentInput.toLowerCase().includes('seo')) {
        const titleLength = analysis?.seoIntelligence?.titleStrategy?.length || 0;
        response += `The SEO analysis shows a title length of ${titleLength} characters. `;
        response += titleLength < 30 ? "Consider making the title longer for better SEO." : 
                   titleLength > 60 ? "The title might be too long for search results." : 
                   "The title length looks good!";
      } else if (currentInput.toLowerCase().includes('keyword')) {
        const totalKeywords = analysis?.keywordIntelligence?.totalKeywords || 0;
        response += `I found ${totalKeywords} total keywords. The top commercial keywords could be great opportunities for your business.`;
      } else if (currentInput.toLowerCase().includes('market') || currentInput.toLowerCase().includes('industry')) {
        const industry = analysis?.marketIntelligence?.primaryIndustry || "unknown";
        const score = analysis?.opportunityAnalysis?.marketOpportunityScore || 0;
        response += `This appears to be in the ${industry} industry with a market opportunity score of ${score}/100.`;
      } else {
        response += "I can help you understand the SEO metrics, keyword opportunities, and market positioning. Try asking about specific areas like 'SEO performance' or 'keyword analysis'.";
      }

      const botMessage = {
        id: Date.now() + 1,
        type: 'bot',
        message: response,
        timestamp: new Date().toISOString(),
      };

      setChatMessages(prev => [...prev, botMessage]);
    }, 1000);
  };

  // Quick analysis
  const handleQuickAnalysis = async (url) => {
    setAnalysisUrl(url);
    setIsLoading(true);
    
    try {
      const response = await fetch(`${SEO_API_BASE}/api/analyze/competitor`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ url }),
      });

      const data = await response.json();
      
      // Transform the data to match the expected format
      const transformedData = {
        success: data.success,
        analysis: data.data,
        formatted_data: formatSEODataForTables(data.data),
        quick: true
      };
      
      // Extract enhanced data for new features
      const enhanced = extractEnhancedData(data.data);
      setEnhancedData(enhanced);
      setRawHtmlContent(data.data?.htmlContent || data.data?.rawHtml || '');
      setCurrentAnalyzedUrl(url);
      
      setAnalysisResult(transformedData);
      setActiveTab('results');
    } catch (error) {
      console.error('Quick analysis error:', error);
      setAnalysisResult({
        success: false,
        error: error.message,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Parse and format AI insights into readable content
  const parseAIInsights = (insights) => {
    if (!insights) return null;

    // If it's a string, try to parse it as JSON
    if (typeof insights === 'string') {
      try {
        insights = JSON.parse(insights);
      } catch (e) {
        // If it's not valid JSON, return the string as is
        return insights;
      }
    }

    // Now we have a JSON object, extract the values using the keys
    let readableContent = "";

    // Strategic Overview
    if (insights.strategic_overview) {
      readableContent += `🎯 **Strategic Overview**\n\n${insights.strategic_overview}\n\n`;
    }

    // SEO Insights (handle nested object)
    if (insights.seo_insights) {
      readableContent += `🔍 **SEO Analysis**\n\n`;
      
      if (insights.seo_insights.strengths && insights.seo_insights.strengths.length > 0) {
        readableContent += `✅ **Strengths:**\n`;
        insights.seo_insights.strengths.forEach(strength => {
          readableContent += `• ${strength}\n`;
        });
        readableContent += `\n`;
      }

      if (insights.seo_insights.weaknesses && insights.seo_insights.weaknesses.length > 0) {
        readableContent += `⚠️ **Areas for Improvement:**\n`;
        insights.seo_insights.weaknesses.forEach(weakness => {
          readableContent += `• ${weakness}\n`;
        });
        readableContent += `\n`;
      }

      if (insights.seo_insights.critical_gaps) {
        readableContent += `🚨 **Critical Issues:**\n• ${insights.seo_insights.critical_gaps}\n\n`;
      }
    }

    // Market Position (handle nested object)
    if (insights.market_position) {
      readableContent += `📊 **Market Position**\n\n`;
      
      if (insights.market_position.current_standing) {
        readableContent += `• **Current Standing:** ${insights.market_position.current_standing}\n`;
      }
      if (insights.market_position.positioning) {
        readableContent += `• **Positioning:** ${insights.market_position.positioning}\n`;
      }
      if (insights.market_position.target_audience) {
        readableContent += `• **Target Audience:** ${insights.market_position.target_audience}\n`;
      }
      if (insights.market_position.market_share) {
        readableContent += `• **Market Share:** ${insights.market_position.market_share}\n`;
      }
      
      if (insights.market_position.key_weaknesses && insights.market_position.key_weaknesses.length > 0) {
        readableContent += `\n**Key Weaknesses:**\n`;
        insights.market_position.key_weaknesses.forEach(weakness => {
          readableContent += `• ${weakness}\n`;
        });
      }
      readableContent += `\n`;
    }

    // Keyword Strategy (handle nested object)
    if (insights.keyword_strategy) {
      readableContent += `🎯 **Keyword Strategy**\n\n`;
      
      if (insights.keyword_strategy.primary_focus) {
        readableContent += `• **Primary Focus:** ${insights.keyword_strategy.primary_focus}\n\n`;
      }

      if (insights.keyword_strategy.gaps && insights.keyword_strategy.gaps.length > 0) {
        readableContent += `**Keyword Gaps:**\n`;
        insights.keyword_strategy.gaps.forEach(gap => {
          readableContent += `• ${gap}\n`;
        });
        readableContent += `\n`;
      }

      if (insights.keyword_strategy.opportunities && insights.keyword_strategy.opportunities.length > 0) {
        readableContent += `**Opportunities:**\n`;
        insights.keyword_strategy.opportunities.forEach(opp => {
          readableContent += `• ${opp}\n`;
        });
        readableContent += `\n`;
      }
    }

    // Competitive Advantages (handle nested object)
    if (insights.competitive_advantages) {
      readableContent += `🏆 **Competitive Analysis**\n\n`;
      
      if (insights.competitive_advantages.current && insights.competitive_advantages.current.length > 0) {
        readableContent += `**Current Advantages:**\n`;
        insights.competitive_advantages.current.forEach(advantage => {
          readableContent += `• ${advantage}\n`;
        });
        readableContent += `\n`;
      }

      if (insights.competitive_advantages.missing_elements && insights.competitive_advantages.missing_elements.length > 0) {
        readableContent += `**Missing Elements:**\n`;
        insights.competitive_advantages.missing_elements.forEach(element => {
          readableContent += `• ${element}\n`;
        });
        readableContent += `\n`;
      }
    }

    // Recommendations (handle nested object)
    if (insights.recommendations) {
      readableContent += `💡 **Strategic Recommendations**\n\n`;

      if (insights.recommendations.strategic && insights.recommendations.strategic.length > 0) {
        readableContent += `**Strategic Actions:**\n`;
        insights.recommendations.strategic.forEach((rec, index) => {
          readableContent += `${index + 1}. ${rec}\n`;
        });
        readableContent += `\n`;
      }

      if (insights.recommendations.tactical && insights.recommendations.tactical.length > 0) {
        readableContent += `**Tactical Actions:**\n`;
        insights.recommendations.tactical.forEach((rec, index) => {
          readableContent += `${index + 1}. ${rec}\n`;
        });
        readableContent += `\n`;
      }
    }

    // Quick Actions
    if (insights.quick_actions) {
      readableContent += `⚡ **Quick Wins - Take Action Today**\n\n`;
      if (Array.isArray(insights.quick_actions)) {
        insights.quick_actions.forEach((action, index) => {
          readableContent += `${index + 1}. ${action}\n`;
        });
      } else {
        readableContent += `${insights.quick_actions}\n`;
      }
      readableContent += `\n`;
    }

    // Opportunity Breakdown (handle nested object)
    if (insights.opportunity_breakdown) {
      readableContent += `📈 **Market Opportunity Analysis**\n\n`;
      
      if (insights.opportunity_breakdown.score_analysis) {
        readableContent += `${insights.opportunity_breakdown.score_analysis}\n\n`;
      }

      if (insights.opportunity_breakdown.market_potential) {
        readableContent += `**Market Potential:** ${insights.opportunity_breakdown.market_potential}\n\n`;
      }

      if (insights.opportunity_breakdown.key_factors && insights.opportunity_breakdown.key_factors.length > 0) {
        readableContent += `**Key Factors:**\n`;
        insights.opportunity_breakdown.key_factors.forEach(factor => {
          readableContent += `• ${factor}\n`;
        });
        readableContent += `\n`;
      }
    }

    // Next Steps (handle nested object)
    if (insights.next_steps) {
      readableContent += `📅 **Implementation Roadmap**\n\n`;

      if (insights.next_steps['30_days'] && insights.next_steps['30_days'].length > 0) {
        readableContent += `**First 30 Days:**\n`;
        insights.next_steps['30_days'].forEach(step => {
          readableContent += `• ${step}\n`;
        });
        readableContent += `\n`;
      }

      if (insights.next_steps['60_days'] && insights.next_steps['60_days'].length > 0) {
        readableContent += `**60 Days:**\n`;
        insights.next_steps['60_days'].forEach(step => {
          readableContent += `• ${step}\n`;
        });
        readableContent += `\n`;
      }

      if (insights.next_steps['90_days'] && insights.next_steps['90_days'].length > 0) {
        readableContent += `**90 Days:**\n`;
        insights.next_steps['90_days'].forEach(step => {
          readableContent += `• ${step}\n`;
        });
        readableContent += `\n`;
      }
    }

    return readableContent || "No insights available";
  };

  // Render AI insights in a beautiful format
  const renderAIInsights = (insights) => {
    if (!insights) return null;

    // Convert JSON to readable text
    const readableContent = parseAIInsights(insights);

    if (!readableContent) {
      return (
        <div className="insight-card">
          <h3>🤖 AI Analysis Results</h3>
          <div className="insight-content">
            <p>No insights available</p>
          </div>
        </div>
      );
    }

    // Render the readable content with proper formatting
    return (
      <div className="ai-insights-readable">
        <div className="insights-content">
          {readableContent.split('\n\n').map((section, index) => {
            if (!section.trim()) return null;
            
            // Check if it's a header (starts with emoji and **)
            if (section.includes('**') && section.match(/^[🎯📊🔍💡⚡📅📈🏆]/)) {
              const [header, ...content] = section.split('\n');
              const cleanHeader = header.replace(/\*\*/g, '');
              
              return (
                <div key={index} className="insight-section-readable">
                  <h3>{cleanHeader}</h3>
                  <div className="section-content">
                    {content.join('\n').split('\n').map((line, lineIndex) => {
                      if (!line.trim()) return null;
                      
                      // Handle bullet points
                      if (line.startsWith('• ')) {
                        return (
                          <div key={lineIndex} className="bullet-point">
                            <span className="bullet">•</span>
                            <span className="bullet-text">{line.substring(2)}</span>
                          </div>
                        );
                      }
                      
                      // Handle numbered lists
                      if (line.match(/^\d+\. /)) {
                        const [number, ...text] = line.split('. ');
                        return (
                          <div key={lineIndex} className="numbered-point">
                            <span className="number">{number}.</span>
                            <span className="numbered-text">{text.join('. ')}</span>
                          </div>
                        );
                      }
                      
                      // Handle sub-headers (bold text)
                      if (line.includes('**')) {
                        const cleanLine = line.replace(/\*\*/g, '');
                        return (
                          <h4 key={lineIndex} className="sub-header">{cleanLine}</h4>
                        );
                      }
                      
                      // Regular paragraph
                      return (
                        <p key={lineIndex} className="insight-paragraph">{line}</p>
                      );
                    })}
                  </div>
                </div>
              );
            }
            
            // Regular content section
            return (
              <div key={index} className="insight-text">
                <p>{section}</p>
              </div>
            );
          })}
        </div>
      </div>
    );
  };


  // Render analysis tables
  const renderTable = (tableData, title) => {
    if (!tableData || !tableData.headers || !tableData.rows) return null;

    return (
      <div className="analysis-table-container">
        <h4 className="table-title">{title}</h4>
        <div className="table-wrapper">
          <table className="analysis-table">
            <thead>
              <tr>
                {tableData.headers.map((header, index) => (
                  <th key={index}>{header}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tableData.rows.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {row.map((cell, cellIndex) => (
                    <td key={cellIndex}>{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className="market-research-page">
      {/* Header */}
      <div className="research-header">
        <div className="header-content">
          <div className="header-text">
            <div className="header-logo-section">
              <img src="/seoblogger.png" alt="SEO Blogger" className="header-logo" />
              <div className="header-title-group">
                <h1>🔍 Market Research Intelligence</h1>
                <p>Analyze competitors, discover opportunities, and get AI-powered insights to dominate your market</p>
              </div>
            </div>
          </div>
          <div className="header-visual">
            <div className="floating-metrics">
              <div className="metric-card">
                <div className="metric-icon">📊</div>
                <div className="metric-text">SEO Analysis</div>
              </div>
              <div className="metric-card">
                <div className="metric-icon">🎯</div>
                <div className="metric-text">Market Intel</div>
              </div>
              <div className="metric-card">
                <div className="metric-icon">🤖</div>
                <div className="metric-text">AI Insights</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Error Banner */}
      {(analysisError || insightsError) && (
        <div className="error-banner">
          <div className="error-banner-content">
            {analysisError && (
              <div className="error-item">
                <span className="error-label">Analysis Error:</span>
                <span className="error-text">{analysisError}</span>
              </div>
            )}
            {insightsError && (
              <div className="error-item">
                <span className="error-label">AI Insights Error:</span>
                <span className="error-text">{insightsError}</span>
              </div>
            )}
            <button 
              onClick={() => {
                setAnalysisError(null);
                setInsightsError(null);
              }}
              className="error-close"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* Navigation Tabs */}
      <div className="research-nav">
        <div className="nav-tabs">
          <button 
            className={`nav-tab ${activeTab === 'analyze' ? 'active' : ''}`}
            onClick={() => setActiveTab('analyze')}
          >
            🔬 Analyze
          </button>
          <button 
            className={`nav-tab ${activeTab === 'results' ? 'active' : ''}`}
            onClick={() => setActiveTab('results')}
            disabled={!analysisResult}
          >
            📋 Results
          </button>
          <button 
            className={`nav-tab ${activeTab === 'insights' ? 'active' : ''}`}
            onClick={() => setActiveTab('insights')}
            disabled={!aiInsights}
          >
            🤖 AI Insights
          </button>
          <button 
            className={`nav-tab ${activeTab === 'chat' ? 'active' : ''}`}
            onClick={() => setActiveTab('chat')}
            disabled={!analysisResult?.success}
          >
            💬 AI Assistant
          </button>
          <button 
            className={`nav-tab ${activeTab === 'html-preview' ? 'active' : ''}`}
            onClick={() => setActiveTab('html-preview')}
            disabled={!rawHtmlContent}
          >
            🔧 HTML Preview
          </button>
          <button 
            className={`nav-tab ${activeTab === 'page-preview' ? 'active' : ''}`}
            onClick={() => setActiveTab('page-preview')}
            disabled={!currentAnalyzedUrl}
          >
            🌐 Live Preview
          </button>
          <button 
            className={`nav-tab ${activeTab === 'history' ? 'active' : ''}`}
            onClick={() => setActiveTab('history')}
          >
            📚 History
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="research-content">
        {/* Analyze Tab */}
        {activeTab === 'analyze' && (
          <div className="analyze-section">
            <div className="analyze-hero">
              <div className="analyze-hero-content">
                <div className="analyze-icon-wrapper">
                  <div className="analyze-icon">
                    <span className="icon-search">🔍</span>
                    <div className="icon-pulse"></div>
                  </div>
                </div>
                
                <div className="analyze-text-section">
                  <h2 className="analyze-title">Discover Your Market Intelligence</h2>
                  <p className="analyze-subtitle">
                    Enter any website URL to unlock comprehensive SEO analysis, competitive insights, 
                    and AI-powered market intelligence in seconds
                  </p>
                </div>

                <div className="analyze-form-wrapper">
                  <div className="url-input-container">
                    <div className="input-icon">🌐</div>
                    <input
                      type="url"
                      value={analysisUrl}
                      onChange={(e) => setAnalysisUrl(e.target.value)}
                      placeholder="https://example.com"
                      className="url-input-hero"
                      disabled={isLoading}
                      onKeyPress={(e) => e.key === 'Enter' && handleAnalyze()}
                    />
                    <button 
                      onClick={handleAnalyze}
                      disabled={isLoading || !analysisUrl.trim()}
                      className="analyze-btn-hero"
                    >
                      {isLoading ? (
                        <div className="loading-content">
                          <div className="spinner-hero"></div>
                          <span>Analyzing...</span>
                        </div>
                      ) : (
                        <div className="button-content">
                          <span className="btn-icon">🚀</span>
                          <span>Analyze Now</span>
                        </div>
                      )}
                    </button>
                  </div>
                  
                  <div className="analyze-features">
                    <div className="feature-badge">
                      <span className="badge-icon">⚡</span>
                      <span>Instant Results</span>
                    </div>
                    <div className="feature-badge">
                      <span className="badge-icon">🎯</span>
                      <span>SEO Insights</span>
                    </div>
                    <div className="feature-badge">
                      <span className="badge-icon">🤖</span>
                      <span>AI Analysis</span>
                    </div>
                  </div>
                </div>

                {/* Enhanced Quick Examples */}
                <div className="quick-examples-hero">
                  <p className="examples-label">Try these popular examples:</p>
                  <div className="example-buttons-grid">
                    {[
                      { url: 'https://brilliant.org', name: 'Brilliant', category: 'Education' },
                      { url: 'https://github.com', name: 'GitHub', category: 'Development' },
                      { url: 'https://notion.so', name: 'Notion', category: 'Productivity' },
                      { url: 'https://figma.com', name: 'Figma', category: 'Design' }
                    ].map((example) => (
                      <button
                        key={example.url}
                        onClick={() => handleQuickAnalysis(example.url)}
                        className="example-btn-hero"
                        disabled={isLoading}
                      >
                        <div className="example-content">
                          <span className="example-name">{example.name}</span>
                          <span className="example-category">{example.category}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Results Tab */}
        {activeTab === 'results' && (
          <div className="results-section">
            {analysisResult?.success ? (
              <div className="results-container">
                {/* Summary Cards */}
                <div className="summary-cards">
                  <div className="summary-card">
                    <div className="card-icon">🎯</div>
                    <div className="card-content">
                      <h4>Opportunity Score</h4>
                      <div className="score">
                        {analysisResult.analysis?.opportunityAnalysis?.marketOpportunityScore || 
                         analysisResult.insights?.opportunity_score || 0}/100
                      </div>
                    </div>
                  </div>
                  <div className="summary-card">
                    <div className="card-icon">🏢</div>
                    <div className="card-content">
                      <h4>Industry</h4>
                      <div className="industry">
                        {analysisResult.analysis?.marketIntelligence?.primaryIndustry || 
                         analysisResult.insights?.primary_industry || 'Unknown'}
                      </div>
                    </div>
                  </div>
                  <div className="summary-card">
                    <div className="card-icon">🔑</div>
                    <div className="card-content">
                      <h4>Keywords</h4>
                      <div className="keywords-count">
                        {analysisResult.analysis?.keywordIntelligence?.totalKeywords || 
                         analysisResult.insights?.top_keywords?.length || 0}
                      </div>
                    </div>
                  </div>
                  <div className="summary-card">
                    <div className="card-icon">💰</div>
                    <div className="card-content">
                      <h4>Commercial Intent</h4>
                      <div className="commercial-score">
                        {analysisResult.analysis?.keywordIntelligence?.commercialIntentScore || 0}%
                      </div>
                    </div>
                  </div>
                </div>

                {/* Detailed Tables */}
                {analysisResult.formatted_data && (
                  <div className="detailed-analysis">
                    {renderTable(analysisResult.formatted_data.seo_metrics, '🔍 SEO Content Analysis')}
                    {renderTable(analysisResult.formatted_data.market_analysis, '🏢 Market Intelligence')}
                    {renderTable(analysisResult.formatted_data.keyword_analysis, '🎯 Keyword Opportunities')}
                  </div>
                )}

                {/* Quick Insights for Quick Analysis */}
                {analysisResult.quick && analysisResult.insights && (
                  <div className="quick-insights">
                    <h3>Quick Insights</h3>
                    <div className="insights-grid">
                      <div className="insight-item">
                        <span className="insight-label">Domain:</span>
                        <span className="insight-value">{analysisResult.insights.domain}</span>
                      </div>
                      <div className="insight-item">
                        <span className="insight-label">Title Length:</span>
                        <span className="insight-value">{analysisResult.insights.title_length} chars</span>
                      </div>
                      <div className="insight-item">
                        <span className="insight-label">Has H1:</span>
                        <span className="insight-value">{analysisResult.insights.has_h1 ? '✅' : '❌'}</span>
                      </div>
                      <div className="insight-item">
                        <span className="insight-label">Meta Optimized:</span>
                        <span className="insight-value">{analysisResult.insights.meta_optimized ? '✅' : '❌'}</span>
                      </div>
                    </div>
                    
                    {analysisResult.recommendations && (
                      <div className="recommendations">
                        <h4>Recommendations</h4>
                        <ul>
                          {analysisResult.recommendations.map((rec, index) => (
                            <li key={index}>{rec}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : analysisResult?.error ? (
              <div className="error-message">
                <div className="error-icon">❌</div>
                <h3>Analysis Failed</h3>
                <p>{analysisResult.error}</p>
                <button onClick={() => setActiveTab('analyze')} className="retry-btn">
                  Try Again
                </button>
              </div>
            ) : (
              <div className="no-results">
                <div className="no-results-icon">📊</div>
                <h3>No Analysis Yet</h3>
                <p>Run an analysis to see detailed results here</p>
                <button onClick={() => setActiveTab('analyze')} className="start-btn">
                  Start Analysis
                </button>
              </div>
            )}
          </div>
        )}

        {/* AI Insights Tab */}
        {activeTab === 'insights' && (
          <div className="insights-section">
            {aiInsights ? (
              <div className="insights-container">
                {/* AI Analysis Header */}
                <div className="insights-header">
                  <div className="insights-title">
                    <h2>🤖 AI-Powered Analysis</h2>
                    <p>Claude's comprehensive competitive intelligence report</p>
                  </div>
                  {isLoadingInsights && (
                    <div className="insights-loading">
                      <div className="spinner"></div>
                      <span>Generating insights...</span>
                    </div>
                  )}
                </div>

                {/* Formatted AI Insights Display */}
                {renderAIInsights(aiInsights)}

                {/* Raw Analysis Data (for debugging) */}
                <details className="raw-data">
                  <summary>🔧 Raw AI Response (Debug)</summary>
                  <pre>{JSON.stringify(aiInsights, null, 2)}</pre>
                </details>
              </div>
            ) : isLoadingInsights ? (
              <div className="insights-loading-state">
                <div className="loading-animation">
                  <div className="spinner large"></div>
                  <h3>🤖 AI is analyzing...</h3>
                  <p>Claude is generating comprehensive insights about your competitor</p>
                </div>
              </div>
            ) : insightsError ? (
              <div className="error-message">
                <div className="error-icon">❌</div>
                <h3>AI Insights Error</h3>
                <p>{insightsError}</p>
                <button onClick={() => getAIInsights(analysisUrl)} className="retry-btn">
                  🔄 Retry AI Analysis
                </button>
              </div>
            ) : (
              <div className="insights-placeholder">
                <div className="placeholder-icon">🤖</div>
                <h3>AI Insights Ready</h3>
                <p>Complete an analysis to get AI-powered strategic insights and recommendations</p>
              </div>
            )}
          </div>
        )}

        {/* Chat Tab */}
        {activeTab === 'chat' && (
          <div className="chat-section">
            {analysisResult?.success ? (
              <div className="chat-container">
                <div className="chat-messages">
                  {chatMessages.map((msg) => (
                    <div key={msg.id} className={`chat-message ${msg.type}`}>
                      <div className="message-avatar">
                        {msg.type === 'user' ? '👤' : '🤖'}
                      </div>
                      <div className="message-content">
                        <div className="message-text">{msg.message}</div>
                        {msg.recommendations && (
                          <div className="message-recommendations">
                            <h5>Recommendations:</h5>
                            <ul>
                              {msg.recommendations.map((rec, index) => (
                                <li key={index}>{rec}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        <div className="message-time">
                          {new Date(msg.timestamp).toLocaleTimeString()}
                        </div>
                      </div>
                    </div>
                  ))}

                </div>
                
                <form onSubmit={handleChatSubmit} className="chat-input-form">
                  <input
                    type="text"
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    placeholder="Ask about SEO insights, market opportunities, or competitive strategies..."
                    className="chat-input"
                  />
                  <button 
                    type="submit" 
                    disabled={!chatInput.trim()}
                    className="chat-send-btn"
                  >
                    📤
                  </button>
                </form>
              </div>
            ) : (
              <div className="chat-placeholder">
                <div className="placeholder-icon">💬</div>
                <h3>AI Assistant Ready</h3>
                <p>Complete an analysis first to start chatting with the AI about insights and recommendations</p>
              </div>
            )}
          </div>
        )}

        {/* HTML Preview Tab */}
        {activeTab === 'html-preview' && (
          <div className="html-preview-section">
            {rawHtmlContent ? (
              <div className="html-preview-container">
                <div className="preview-header">
                  <h3>📄 Raw HTML Content</h3>
                  <div className="preview-controls">
                    <button 
                      onClick={() => navigator.clipboard.writeText(rawHtmlContent)}
                      className="copy-btn"
                    >
                      📋 Copy HTML
                    </button>
                    <button 
                      onClick={() => {
                        const blob = new Blob([rawHtmlContent], { type: 'text/html' });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `${currentAnalyzedUrl.replace(/[^a-zA-Z0-9]/g, '_')}.html`;
                        a.click();
                        URL.revokeObjectURL(url);
                      }}
                      className="download-btn"
                    >
                      💾 Download HTML
                    </button>
                  </div>
                </div>
                
                <div className="html-content-display">
                  <pre className="html-code">
                    <code>{rawHtmlContent}</code>
                  </pre>
                </div>

                {/* Enhanced Data Preview */}
                {enhancedData && (
                  <div className="enhanced-data-section">
                    <h4>🔍 Extracted Technical Details</h4>
                    
                    {/* Page Content Summary */}
                    <div className="data-card">
                      <h5>📋 Page Content</h5>
                      <div className="data-grid">
                        <div className="data-item">
                          <span className="data-label">Title:</span>
                          <span className="data-value">{enhancedData.pageContent.title || 'Not found'}</span>
                        </div>
                        <div className="data-item">
                          <span className="data-label">Meta Description:</span>
                          <span className="data-value">{enhancedData.pageContent.metaDescription || 'Not found'}</span>
                        </div>
                        <div className="data-item">
                          <span className="data-label">Images Found:</span>
                          <span className="data-value">{enhancedData.pageContent.allImages.length}</span>
                        </div>
                        <div className="data-item">
                          <span className="data-label">Links Found:</span>
                          <span className="data-value">{enhancedData.pageContent.allLinks.length}</span>
                        </div>
                      </div>
                    </div>

                    {/* Technical Details */}
                    <div className="data-card">
                      <h5>⚙️ Technical Details</h5>
                      <div className="data-grid">
                        <div className="data-item">
                          <span className="data-label">Charset:</span>
                          <span className="data-value">{enhancedData.technicalDetails.charset || 'Not specified'}</span>
                        </div>
                        <div className="data-item">
                          <span className="data-label">Language:</span>
                          <span className="data-value">{enhancedData.technicalDetails.language || 'Not specified'}</span>
                        </div>
                        <div className="data-item">
                          <span className="data-label">Viewport:</span>
                          <span className="data-value">{enhancedData.technicalDetails.viewport || 'Not set'}</span>
                        </div>
                        <div className="data-item">
                          <span className="data-label">Robots:</span>
                          <span className="data-value">{enhancedData.technicalDetails.robots || 'Not specified'}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="html-preview-placeholder">
                <div className="placeholder-icon">📄</div>
                <h3>No HTML Content</h3>
                <p>Run an analysis to view the raw HTML content and technical details</p>
              </div>
            )}
          </div>
        )}

        {/* Page Preview Tab */}
        {activeTab === 'page-preview' && (
          <div className="page-preview-section">
            {currentAnalyzedUrl ? (
              <div className="page-preview-container">
                <div className="preview-header">
                  <h3>🌐 Live Page Preview</h3>
                  <div className="preview-controls">
                    <span className="current-url">Viewing: {currentAnalyzedUrl}</span>
                    <button 
                      onClick={() => window.open(currentAnalyzedUrl, '_blank')}
                      className="open-external-btn"
                    >
                      🔗 Open in New Tab
                    </button>
                    <button 
                      onClick={() => setShowPageModal(true)}
                      className="open-modal-btn"
                    >
                      🖼️ Open in Modal
                    </button>
                  </div>
                </div>
                
                <div className="iframe-container">
                  <iframe
                    src={currentAnalyzedUrl}
                    title="Page Preview"
                    className="page-preview-iframe"
                    sandbox="allow-same-origin allow-scripts allow-forms"
                  />
                </div>
              </div>
            ) : (
              <div className="page-preview-placeholder">
                <div className="placeholder-icon">🌐</div>
                <h3>No Page to Preview</h3>
                <p>Run an analysis to preview the live website</p>
              </div>
            )}
          </div>
        )}

        {/* Page Modal */}
        {showPageModal && (
          <div className="page-modal-overlay" onClick={() => setShowPageModal(false)}>
            <div className="page-modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>🌐 {currentAnalyzedUrl}</h3>
                <button 
                  onClick={() => setShowPageModal(false)}
                  className="modal-close-btn"
                >
                  ✕
                </button>
              </div>
              <div className="modal-iframe-container">
                <iframe
                  src={currentAnalyzedUrl}
                  title="Page Modal Preview"
                  className="modal-iframe"
                  sandbox="allow-same-origin allow-scripts allow-forms"
                />
              </div>
            </div>
          </div>
        )}

        {/* History Tab */}
        {activeTab === 'history' && (
          <div className="history-section">
            <h3>Recent Analyses</h3>
            {recentAnalyses.length > 0 ? (
              <div className="history-list">
                {recentAnalyses.map((analysis, index) => (
                  <div key={index} className="history-item">
                    <div className="history-info">
                      <div className="history-domain">{analysis.domain}</div>
                      <div className="history-url">{analysis.url}</div>
                      <div className="history-date">
                        {new Date(analysis.timestamp).toLocaleDateString()}
                      </div>
                    </div>
                    <div className="history-score">
                      <div className="score-circle">
                        {analysis.score}
                      </div>
                    </div>
                    <button 
                      onClick={() => handleQuickAnalysis(analysis.url)}
                      className="reanalyze-btn"
                      disabled={isLoading}
                    >
                      🔄 Re-analyze
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-history">
                <div className="empty-icon">📚</div>
                <h4>No Analysis History</h4>
                <p>Your recent analyses will appear here</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MarketResearchPage; 